<template>
  <mailbox-settings-layout :name="name">
    <el-card class="mailbox-settings-auto-forwarding">
      <template #header>
        <h3>Auto-Forwarding</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <div class="row">
          <div class="col-12 forwarding-emails-list">
            <div class="forwarding-emails-list__header">
              <h4>Auto-Forwarding Emails</h4>
              <dd-icon-button 
                icon="plus" 
                type="circle" 
                size="medium" 
                title="Add new forwarding email"
                @click="goToAutoForwardingSetup()" 
              />
            </div>
            <div v-if="hasForwardingEmailAddresses">
              <mailbox-settings-auto-forwarding-email
                v-for="(email, index) in forwardingEmailAddresses"
                :key="index"
                :email="email"
                @remove="confirmRemoveEmailAddress(email)"
              />
            </div>
            <p v-else class="forwarding-emails-list__empty-message">
              There are no emails forwarding into this mailbox.
              <router-link :to="RouteAssistant.getMailboxVerificationIntroRoute(accountID, id)" class="just-a-link">Add your first</router-link>!
            </p>
          </div>
        </div>
        <div class="dd-divider" />
        <p>
          This mailbox will receive emails forwarded to this address:
        </p>
        <clipboard-text :text="incomingEmailAddress" class="incoming-email-address" />
        <p><br></p>
        <p class="small">
          If this address has been compromised, you 
          can <a href="#" class="just-a-link" @click.stop.prevent="confirmNewIncomingEmailAddress">generate a new address</a>.
          You will need to update the auto-forwarding rule on your existing forwarding addresses.
        </p>
        <p><br></p>
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import MailboxSettingsAutoForwardingEmail from './components/mailbox-settings-auto-forwarding-email.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'MailboxSetupAutoForwarding',

  components: {
    MailboxSettingsLayout,
    MailboxSettingsAutoForwardingEmail,
    EntitySettingsLoading,
  },

  data() {
    return {
      RouteAssistant,
      isLoading: true,
      name: '',
      incomingEmailAddress: '',
      forwardingEmailAddresses: [] as string[],
    }
  },

  head(): any {
    return {
      title: `Auto-Forwarding - ${this.name}`,
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    hasForwardingEmailAddresses(): boolean {
      return this.forwardingEmailAddresses.length > 0
    },
  },

  async created() {
    this.isLoading = true
    await this.getAutoForwardingInfo()
    this.isLoading = false
  },

  methods: {
    async getAutoForwardingInfo() {
      try {
        const response = await MailboxesAPI.get_AutoForwarding(this.id)
        this.name = response.name
        this.incomingEmailAddress = response.incomingEmailAddress
        this.forwardingEmailAddresses = response.listForwardingEmailAddresses
      } catch (error) {
        this.$error.emit(error)
      }
    },
    goToAutoForwardingSetup() {
      // Auto-route to auto-forwarding setup...
      RouteAssistant.goToMailboxVerificationIntroRoute(
        this.$router,
        this.accountID,
        this.id,
      )
    },
    async confirmRemoveEmailAddress(email: string) {
      this.$confirm(
        `Removing ${email} from this list does not disable auto-forwarding. You will
        also need to remove the auto-forwarding rule from your email provider.`,
        'Warning!',
        {
          confirmButtonText: 'I understand, remove this email.',
          cancelButtonText: 'Cancel',
          confirmButtonClass: 'el-button--strawberry',
          type: 'warning',
          center: true,
        },
      ).then(() => {
        this.forwardingEmailAddresses = this.forwardingEmailAddresses.filter(
          e => e !== email,
        )
        this.updateForwardingEmailAddresses()
      })
    },
    confirmNewIncomingEmailAddress() {
      this.$confirm(
        'Once you generate a new forwarding address, the old one will no longer work and existing auto-forwarding rules need to be updated. Are you sure?',
        'Warning!',
        {
          confirmButtonText: 'Yes, generate a new address',
          cancelButtonText: 'No, take me back',
          confirmButtonClass: 'el-button--strawberry',
          type: 'warning',
          center: true,
        },
      ).then(() => {
        this.generateIncomingEmailAddress()
      })
    },
    async generateIncomingEmailAddress(): Promise<void> {
      try {
        const response = await MailboxesAPI.get_GenerateIncomingEmailAddress(
          this.id,
        )
        this.incomingEmailAddress = response.incomingEmailAddress
        this.$messaging.showSuccess(
          'A new forwarding address has been created!',
        )
      } catch (err) {
        this.$error.emit(err, true)
        await this.getAutoForwardingInfo()
      }
    },
    async updateForwardingEmailAddresses(): Promise<void> {
      try {
        const response = await MailboxesAPI.post_AutoForwarding(
          this.id,
          this.forwardingEmailAddresses,
        )
        this.$messaging.showSuccess(response.message)
      } catch (err) {
        this.$error.emit(err, true)
        await this.getAutoForwardingInfo()
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-auto-forwarding {
  margin-bottom: 20px;

  .forwarding-emails-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    &__empty-message {
      color: var(--color-text-third);
      padding-bottom: 20px;
    }
  }
}
</style>
